// Markdown → HTML for the in-app guides (src/content/*.md), such as the
// "Alice in Claude & ChatGPT" page. A deliberately small subset, so the text
// stays easy to edit and the output is safe for v-html:
//
//   # Title              the page title (returned apart, not in `html`)
//   first paragraph      the lead under the title (returned apart, plain text)
//   ## / ### heading     a section, with an id for deep links
//                        (/mcp#toevoegen-aan-claude); ## also goes in `toc`
//   - item / 1. item     bullet and numbered lists, one line per item
//   > [!TIP] Label       a callout: NOTE, TIP, IMPORTANT, WARNING or CAUTION
//   > text …             (GitHub's alert syntax, so the file previews there
//                        too); text after the marker replaces the label
//   ``` … ```            a code block, rendered with a copy button
//   `code`  **bold**  *italic*  [text](https://… | /route | #anchor)
//   <!-- … -->           a note for editors, left out of the page
//
// Everything is HTML-escaped first; only these constructs produce tags.

const CALLOUTS = {
  NOTE: { kind: "note", label: "Goed om te weten" },
  TIP: { kind: "tip", label: "Tip" },
  IMPORTANT: { kind: "important", label: "Belangrijk" },
  WARNING: { kind: "warning", label: "Let op" },
  CAUTION: { kind: "caution", label: "Pas op" },
};

// The label of a code block's copy button; the page swaps it for a moment
// after a successful copy.
export const COPY_LABEL = "Kopieer";

const FENCE = /^```/;
const HEADING = /^(#{1,3})\s+(.+)$/;
const BULLET = /^[-*]\s+(.+)$/;
const NUMBERED = /^(\d+)[.)]\s+(.+)$/;
const QUOTE = /^>/;
const COMMENT = /^<!--/;

export function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// "Toevoegen aan Claude" → "toevoegen-aan-claude". Accents are dropped
// ("Eén project" → "een-project") so a link stays readable when shared.
export function slugify(text) {
  const slug = String(text == null ? "" : text)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "sectie";
}

// The text of a heading or paragraph without its markdown markers, for the
// table of contents and the page lead (both rendered as plain text).
export function plainText(markdown) {
  return String(markdown == null ? "" : markdown)
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .trim();
}

// A link may point to the web (http/https), to a page in Alice (/route) or to
// a heading on this page (#anchor). Anything else (javascript:, data:,
// //host) renders as its text only. `label` and `href` arrive escaped.
function linkHtml(label, href) {
  if (/^https?:\/\/[^\s]+$/i.test(href)) {
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  }
  if (/^\/(?![/\\])[\w\-./?=#&;%]*$/.test(href)) {
    return `<a href="${href}" data-internal="true">${label}</a>`;
  }
  if (/^#[a-z0-9-]+$/.test(href)) {
    return `<a href="${href}">${label}</a>`;
  }
  return label;
}

// Inline markdown of one line. Code spans first, so nothing inside them is
// formatted, then links, bold and italic on the escaped text.
export function renderInline(text) {
  return String(text == null ? "" : text)
    .split(/(`[^`]+`)/)
    .map((part) => {
      if (/^`[^`]+`$/.test(part)) {
        return `<code>${escapeHtml(part.slice(1, -1))}</code>`;
      }
      return escapeHtml(part)
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label, href) =>
          linkHtml(label, href)
        )
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(
          /(^|[^*\w])\*([^*\s](?:[^*]*[^*\s])?)\*(?![*\w])/g,
          "$1<em>$2</em>"
        );
    })
    .join("");
}

function startsBlock(trimmed) {
  return (
    FENCE.test(trimmed) ||
    HEADING.test(trimmed) ||
    BULLET.test(trimmed) ||
    NUMBERED.test(trimmed) ||
    QUOTE.test(trimmed) ||
    COMMENT.test(trimmed)
  );
}

function codeBlockHtml(code) {
  return (
    '<div class="guide-code">' +
    `<pre><code>${escapeHtml(code)}</code></pre>` +
    `<button type="button" class="guide-code__copy">${COPY_LABEL}</button>` +
    "</div>"
  );
}

function uniqueId(text, ctx) {
  const base = slugify(plainText(text));
  const seen = ctx.ids.get(base) || 0;
  ctx.ids.set(base, seen + 1);
  return seen ? `${base}-${seen + 1}` : base;
}

function calloutHtml(quoted, ctx) {
  const marker = (quoted[0] || "").match(/^\[!(\w+)\]\s*(.*)$/);
  const type = marker && CALLOUTS[marker[1].toUpperCase()];
  const { kind, label } = type || CALLOUTS.NOTE;
  const custom = type ? marker[2].trim() : "";
  const body = renderBlocks(type ? quoted.slice(1) : quoted, {
    ...ctx,
    top: false,
  });
  return (
    `<div class="guide-callout guide-callout--${kind}" role="note">` +
    `<span class="guide-callout__label">${renderInline(
      custom || label
    )}</span>` +
    `${body}</div>`
  );
}

// Render a run of lines. `ctx.top` is false inside a callout: no title, lead
// or table-of-contents entries there.
function renderBlocks(lines, ctx) {
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (trimmed === "") {
      i += 1;
      continue;
    }

    if (COMMENT.test(trimmed)) {
      while (i < lines.length && !lines[i].includes("-->")) i += 1;
      i += 1;
      continue;
    }

    if (FENCE.test(trimmed)) {
      const code = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i].trim())) {
        code.push(lines[i]);
        i += 1;
      }
      i += 1; // the closing fence
      out.push(codeBlockHtml(code.join("\n")));
      continue;
    }

    const heading = trimmed.match(HEADING);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2].trim();
      i += 1;
      if (level === 1 && ctx.top && !ctx.title) {
        ctx.title = plainText(text);
        continue;
      }
      const tag = level === 1 ? "h2" : `h${level}`;
      const id = uniqueId(text, ctx);
      if (tag === "h2" && ctx.top) ctx.toc.push({ id, text: plainText(text) });
      out.push(`<${tag} id="${id}">${renderInline(text)}</${tag}>`);
      continue;
    }

    if (QUOTE.test(trimmed)) {
      const quoted = [];
      while (i < lines.length && QUOTE.test(lines[i].trim())) {
        quoted.push(lines[i].trim().replace(/^>\s?/, ""));
        i += 1;
      }
      out.push(calloutHtml(quoted, ctx));
      continue;
    }

    const ordered = NUMBERED.test(trimmed);
    if (ordered || BULLET.test(trimmed)) {
      const pattern = ordered ? NUMBERED : BULLET;
      const start = ordered ? Number(trimmed.match(NUMBERED)[1]) : 1;
      const items = [];
      while (i < lines.length && pattern.test(lines[i].trim())) {
        const m = lines[i].trim().match(pattern);
        items.push(`<li>${renderInline(ordered ? m[2] : m[1])}</li>`);
        i += 1;
      }
      const tag = ordered ? "ol" : "ul";
      const startAttr = ordered && start !== 1 ? ` start="${start}"` : "";
      out.push(`<${tag}${startAttr}>${items.join("")}</${tag}>`);
      continue;
    }

    // A paragraph: consecutive lines that start no other block.
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !startsBlock(lines[i].trim())
    ) {
      para.push(lines[i].trim());
      i += 1;
    }
    const text = para.join(" ");
    if (ctx.top && ctx.title && !ctx.lead && out.length === 0) {
      ctx.lead = plainText(text);
    } else {
      out.push(`<p>${renderInline(text)}</p>`);
    }
  }
  return out.join("");
}

// Parse a guide into { title, lead, toc: [{ id, text }], html }.
export function parseGuide(markdown) {
  const ctx = { top: true, title: "", lead: "", toc: [], ids: new Map() };
  const lines = String(markdown == null ? "" : markdown)
    .replace(/\r\n?/g, "\n")
    .split("\n");
  const html = renderBlocks(lines, ctx);
  return { title: ctx.title, lead: ctx.lead, toc: ctx.toc, html };
}
